import React, { useState } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { ARRAY_ERROR } from 'final-form';
import { Form as FinalForm, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import { useEffect } from 'react';

// Import configs and util modules
import { FormattedMessage, intlShape, injectIntl } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import { nonEmptyArray, composeValidators } from '../../../../util/validators';
import { isUploadImageOverLimitError } from '../../../../util/errors';

// Import shared components
import { Button, Form, AspectRatioWrapper } from '../../../../components';

// Import modules from this directory
import ListingImage from './ListingImage';
import css from './EditListingPhotosForm.module.css';
import IconCollection from '../../../../components/IconCollection/IconCollection';
import IconCard from '../../../../components/SavedCardDetails/IconCard/IconCard';
import { deleteAwsFileApi, deleteImageFromCloudinary, generatePresignedUrl, updateCloudUrls } from '../../../../util/api';
import fileIcon from '../../../../assets/architect-design.png';
import Loader from '../../../../components/Loader/Loader';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { uploadWithPresignedUrl } from '../../../../util/awsUpload';

const ACCEPT_IMAGES = 'image/*,application/pdf';
// const ACCEPT_otherfiles = 'application/pdf';
const ACCEPT_otherfiles = '.rvt,.dwg,.nwd,.ifc,.nwc,.docx,.doc';

const ImageUploadError = props => {
  return props.uploadOverLimit ? (
    <p className={css.error}>
      <FormattedMessage id="EditListingPhotosForm.imageUploadFailed.uploadOverLimit" />
    </p>
  ) : props.uploadImageError ? (
    <p className={css.error}>
      <FormattedMessage id="EditListingPhotosForm.imageUploadFailed.uploadFailed" />
    </p>
  ) : null;
};

// NOTE: PublishListingError and ShowListingsError are here since Photos panel is the last visible panel
// before creating a new listing. If that order is changed, these should be changed too.
// Create and show listing errors are shown above submit button
const PublishListingError = props => {
  return props.error ? (
    <p className={css.error}>
      <FormattedMessage id="EditListingPhotosForm.publishListingFailed" />
    </p>
  ) : null;
};

const ShowListingsError = props => {
  return props.error ? (
    <p className={css.error}>
      <FormattedMessage id="EditListingPhotosForm.showListingFailed" />
    </p>
  ) : null;
};

// Field component that uses file-input to allow user to select images.
export const FieldAddImage = props => {
  const { formApi, onImageUploadHandler, aspectWidth = 1, aspectHeight = 1, ...rest } = props;
  return (
    <Field form={null} {...rest}>
      {fieldprops => {
        const { accept, input, labelOne, labelTwo, disabled: fieldDisabled } = fieldprops;
        const { name, type } = input;
        const onChange = e => {
          const file = e.target.files[0];
          formApi.change(`addImage`, file);
          formApi.blur(`addImage`);
          onImageUploadHandler(file);
        };
        const inputProps = { accept, id: name, name, onChange, type };
        return (
          <div className={css.addImageWrapper}>
            <AspectRatioWrapper width={aspectWidth} height={aspectHeight}>
              {fieldDisabled ? null : <input {...inputProps} className={css.addImageInput} />}
              <label htmlFor={name} className={css.addImage}>
                {/* {label} */}
                <IconCollection name="UPLOAD_IMAGE" />
                <p className={css.dragText}>{labelOne}</p>
                <p className={css.jpegText}>{labelTwo}</p>
              </label>
            </AspectRatioWrapper>
          </div>
        );
      }}
    </Field>
  );
};

// Component that shows listing images from "images" field array
const FieldListingImage = props => {
  const { name, intl, onRemoveImage, aspectWidth, aspectHeight, variantPrefix } = props;
  return (
    <Field name={name}>
      {fieldProps => {
        const { input } = fieldProps;
        const image = input.value;
        return image ? (
          <ListingImage
            image={image}
            key={image?.id?.uuid || image?.id}
            className={css.thumbnail}
            savedImageAltText={intl.formatMessage({
              id: 'EditListingPhotosForm.savedImageAltText',
            })}
            onRemoveImage={() => onRemoveImage(image?.id)}
            aspectWidth={aspectWidth}
            aspectHeight={aspectHeight}
            variantPrefix={variantPrefix}
          />
        ) : null;
      }}
    </Field>
  );
};

export const EditListingPhotosFormComponent = props => {
  const [state, setState] = useState({ imageUploadRequested: false });
  const [submittedImages, setSubmittedImages] = useState([]);
  const [awsFile, setAwsFile] = useState({
    awsFileInProgress: false,
    awsFileRemoveInProgress: false,
    fileProgressPercentage: 0,
  });
  const [cloudImage, setCloudImage] = useState({
    cloudImageInProgress: false,
    cloudImageRemoveInProgress: false,
  });

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      keepDirtyOnReinitialize={true}
      render={formRenderProps => {
        const {
          form,
          className,
          fetchErrors,
          handleSubmit,
          intl,
          invalid,
          onRemoveImage,
          disabled,
          ready,
          saveActionMsg,
          updated,
          updateInProgress,
          touched,
          errors,
          values,
          listingImageConfig,
          setFileUrls,
          fileUrls,
          uploadToAws,
          awsFileUrls,
          setCloudUrls,
          cloudinaryUrls,
          cloudUrls,
        } = formRenderProps;

        useEffect(() => {
          setFileUrls(awsFileUrls);
          setCloudUrls(cloudinaryUrls);
        }, []);

        const { id } = useParams();
        const [error, setError] = useState({ message: false, status: false })
        const uploadCloudinary = file => {
          if (!file) {
            setError({ status: false, message: false });
            return;
          }
          const maxSizeMB = 10;
          const allowedExtensions = ['image/jpeg', 'image/png', 'application/pdf'];
          // Check file size
          if (file.size > maxSizeMB * 1024 * 1024) {
            setError({ status: true, message: `File size exceeds ${maxSizeMB} MB.` });
            return;
          }
          // Check file type
          if (!allowedExtensions.includes(file.type)) {
            setError({ status: true, message: 'Invalid file type. Only JPEG, PNG, and PDF files are allowed.' });
            return;
          }
          setState({ imageUploadRequested: true });
          setError({ message: false, status: false });
          setCloudImage({ ...cloudImage, cloudImageInProgress: true });
          const uploadURL = process.env.REACT_APP_CLOUDINARY_URL;
          const formData = new FormData();
          const fileType = file.type;
          formData.append('file', file);
          formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_PRESET);
          formData.append('resource_type', fileType);
          formData.append('api_key', process.env.REACT_APP_CLOUDINARY_API_KEY);
          formData.append('folder', 'listingImages');
          fetch(uploadURL, { method: 'POST', body: formData })
            .then(response => response.text())
            .then(data => {
              const res = JSON.parse(data);
              const { secure_url, resource_type, public_id } = res || {};
              if (secure_url) {
                //hit api to stote data in sharetribe too
                let temp = [];
                if (Array.isArray(cloudUrls)) {
                  temp = [...cloudUrls, { secure_url, resource_type, public_id }];
                } else {
                  temp = [{ secure_url, resource_type, public_id }];
                }
                let body = {
                  listingId: id,
                  cloudUrls: temp,
                };
                updateCloudUrls(body).then(result => { });
                setCloudUrls(prevPdfUrls => {
                  if (Array.isArray(prevPdfUrls)) {
                    return [...prevPdfUrls, { secure_url, resource_type, public_id }];
                  } else {
                    return [{ secure_url, resource_type, public_id }];
                  }
                });
                setState({ imageUploadRequested: false });
                setTimeout(() => {
                  setCloudImage({ ...cloudImage, cloudImageInProgress: false });
                }, 500);
                setState({ imageUploadRequested: false });
              }
            })
            .catch(err => alert('File uploading failed!'));
        };

        const onImageUploadHandler = file => {
          const { listingImageConfig, onImageUpload } = props || {};
          if (file) {
            onImageUpload({ id: `${file.name}_${Date.now()}`, file }, listingImageConfig)
              .then(() => {
                uploadCloudinary(file);
              })
              .catch(() => {
                setState({ imageUploadRequested: false });
                setCloudImage({ ...cloudImage, cloudImageInProgress: false });
              });
          }
        };
        const getFileExtension = (filename) => {
          // Find the last index of "."
          const lastDotIndex = filename.lastIndexOf(".");

          // If there is no dot in the filename, return an empty string
          if (lastDotIndex === -1) return "";

          // Extract the extension and convert it to lowercase
          return filename.slice(lastDotIndex + 1).toLowerCase();
        };

        function replaceSpacesWithDashes(inputString) {
          return inputString.replace(/ /g, '-');
        }

        const handleAwsFiles = async file => {
          const MAX_FILE_SIZE_MB = 1500; // Maximum file size in megabytes (1.5 GB)
          if (!file) {
            setError({ message: false, status: false });
            return;
          }

          const fileExtension = `.${getFileExtension(file.name)}`;
          // Check file size
          if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            setError({ status: true, message: `File size exceeds ${MAX_FILE_SIZE_MB} MB.` });
            return;
          }

          // Check file type
          if (!ACCEPT_otherfiles.includes(fileExtension)) {
            setError({ status: true, message: 'Invalid file type. Only the following file types are allowed: .rvt, .dwg, .nwd, .ifc, .nwc, .docx, .doc.' });
            return;
          }
          // Clear error if everything is okay
          setError({ status: false, message: false });

          if (file) {
            setAwsFile({ ...awsFile, awsFileInProgress: true });
            const payload = {
              fileName: file.name,
              fileType: getFileExtension(file.name)
            }
            const { url, key } = await generatePresignedUrl(payload)
            const uploadPayload = {
              url, file, fileType: getFileExtension(file.name), setAwsFile, awsFile, key
            }
            const uploadResponse = await uploadWithPresignedUrl(uploadPayload)
            if (uploadResponse)
              setFileUrls(prevFileUrls => {
                if (Array.isArray(prevFileUrls)) {
                  return [...prevFileUrls, uploadResponse];
                } else {
                  return [uploadResponse];
                }
              });
            // uploadToAws(awsFile, setAwsFile, file)
            //   .then(result => {
            //     setFileUrls(prevFileUrls => {
            //       if (Array.isArray(prevFileUrls)) {
            //         return [...prevFileUrls, result];
            //       } else {
            //         return [result];
            //       }
            //     });
            //   })
            //   .catch(() => {
            //     setAwsFile({ ...awsFile, awsFileInProgress: false });
            //   });
          }
        }

        const [removingIndex, setRemovingIndex] = useState(null);
        const removePdfFiles = (index, url) => {
          setRemovingIndex(index);
          setCloudImage({ ...cloudImage, cloudImageRemoveInProgress: true });
          const { public_id, resource_type } = url;
          deleteImageFromCloudinary({ public_id, resource_type })
            .then(res => {
              let existingImageUrls = cloudUrls;
              existingImageUrls.splice(index, 1);

              let body = {
                listingId: id,
                cloudUrls: existingImageUrls,
              };
              updateCloudUrls(body).then(result => { });
              setCloudUrls(existingImageUrls);
              setCloudImage({ ...cloudImage, cloudImageRemoveInProgress: false });
            })
            .catch(err => {
              console.error('Error deleting image from Cloudinary:', err);
              setCloudImage({ ...cloudImage, cloudImageRemoveInProgress: false });
            });
        };

        const removeAwsFiles = async (key, index) => {
          setRemovingIndex(index);
          setAwsFile({ ...awsFile, awsFileRemoveInProgress: true });
          try {
            const res = await deleteAwsFileApi({ key: key });
            const updateFile = fileUrls?.filter(elm => elm.key !== key);
            setFileUrls(updateFile);
            setAwsFile({ ...awsFile, awsFileRemoveInProgress: false });
          } catch (error) {
            console.log('error-->>>', error);
            setAwsFile({ ...awsFile, awsFileRemoveInProgress: false });
          }
        };

        const images = values.images;
        const { aspectWidth = 1, aspectHeight = 1, variantPrefix } = listingImageConfig;

        const { publishListingError, showListingsError, updateListingError, uploadImageError } =
          fetchErrors || {};
        const uploadOverLimit = isUploadImageOverLimitError(uploadImageError);

        // imgs can contain added images (with temp ids) and submitted images with uniq ids.
        const arrayOfImgIds = imgs => imgs.map(i => (typeof i.id === 'string' ? i.imageId : i.id));
        const imageIdsFromProps = arrayOfImgIds(images);
        const imageIdsFromPreviousSubmit = arrayOfImgIds(submittedImages);
        const imageArrayHasSameImages = isEqual(imageIdsFromProps, imageIdsFromPreviousSubmit);
        const submittedOnce = submittedImages.length > 0;
        const pristineSinceLastSubmit = submittedOnce && imageArrayHasSameImages;

        const submitReady = (updated && pristineSinceLastSubmit) || ready;
        const submitInProgress = updateInProgress;
        const submitDisabled =
          invalid ||
          disabled ||
          submitInProgress ||
          state.imageUploadRequested ||
          ready ||
          !cloudUrls ||
          !fileUrls;
        const imagesError = touched.images && errors?.images && errors.images[ARRAY_ERROR];

        const classes = classNames(css.root, className);

        const handleDrop = event => {
          const file = event.dataTransfer.files[0];
          const { listingImageConfig, onImageUpload } = props;
          if (!!file) {
            setState({ imageUploadRequested: true });
            onImageUpload({ id: `${file.name}_${Date.now()}`, file }, listingImageConfig)
              .then(() => {
                uploadCloudinary(file);
                setState({ imageUploadRequested: false });
              })
              .catch(() => {
                setState({ imageUploadRequested: false });
              });
          } else {
            alert('File uploading failed!');
          }
        };

        const handleDropAWS = async event => {
          const file = event.dataTransfer.files[0];
          if (!!file) {
            await handleAwsFiles(file);
          } else {
            alert('File uploading failed!');
          }
        };

        return (
          <Form
            className={classes}
            onSubmit={e => {
              setSubmittedImages(images);
              handleSubmit(e);
            }}
          >
            {updateListingError ? (
              <p className={css.error}>
                <FormattedMessage id="EditListingPhotosForm.updateFailed" />
              </p>
            ) : null}
            {error?.status ? (
              <p className={css.error}>
                <FormattedMessage id={error.message} />
              </p>
            ) : null}

            <p><FormattedMessage id="EditListingPhotosForm.pdfTip" /></p>
            <span className={css.fileLabel}>Add Image and PDF files (Max Size: 10 MB)</span>
            <div className={css.imagesFieldArray}>
              {/* image upload and pdf */}
              <div onDrop={event => handleDrop(event)} onDragOver={event => event.preventDefault()}>
                <FieldAddImage
                  id="addImage"
                  name="addImage"
                  accept={ACCEPT_IMAGES}
                  labelOne={<FormattedMessage id="EditListingPhotosForm.chooseFile" />}
                  labelTwo={<FormattedMessage id="EditListingPhotosForm.imageTypes" />}
                  type="file"
                  disabled={state.imageUploadRequested}
                  formApi={form}
                  onImageUploadHandler={onImageUploadHandler}
                  aspectWidth={aspectWidth}
                  aspectHeight={aspectHeight}
                />
              </div>
              {/* end */}
              {cloudUrls &&
                cloudUrls.length > 0 &&
                cloudUrls.map((file, index) => {
                  const extractUrlPart = url => {
                    if (!url) return null;
                    const parts = url.split('/'); // Split the URL by '/'
                    return parts.slice(-2).join('/'); // Get the last three parts and join them back
                  };
                  const particularFile = file?.secure_url;
                  const getTrimmedUrl = extractUrlPart(particularFile);

                  return (
                    <div key={index}>
                      {removingIndex == index && cloudImage?.cloudImageRemoveInProgress ? (
                        <div className={css.loaderContentRemove}>
                          <div>Removing...</div>
                          <Loader />
                        </div>
                      ) : (
                        <div className={css.pdfBox}>
                          <div className={css.embedBox}>
                            <embed
                              overflow="hidden"
                              src={`https://res.cloudinary.com/${process.env.REACT_APP_CLOUDINARY_NAME
                                }/image/upload/c_thumb,w_${200},h_${200}/f_auto/${getTrimmedUrl}`}
                              loading="lazy"
                            />
                          </div>
                          <button
                            className={css.closeButton}
                            type="button"
                            onClick={() => {
                              removePdfFiles(index, file);
                              !file?.secure_url?.includes('.pdf') &&
                                onRemoveImage(images[index]?.id);
                            }}
                          >
                            <IconCard brand="close" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
            {/* <p className={css.tip}>
              <FormattedMessage id="EditListingPhotosForm.addImagesTip" />
            </p> */}
            <span className={css.fileLabel}>Add BIM and CAD files (Max Size: 1.5 GB)</span>

            <div className={css.pdfDocGrid}>
              {awsFile?.awsFileInProgress ? (
                awsFile?.awsUploading ? <>
                  <div className={css.loaderWrapper}>
                    <div className={css.loaderBox}>
                      <div className={css.progressContainer} >
                        <span className={css.marginLoader}>
                          <Loader />
                        </span>
                        <div className={css.progressLabel}>
                          please wait your file is uploading..
                        </div>
                      </div>
                    </div>
                  </div>

                </> : <div className={css.loaderWrapper}>
                  <div className={css.loaderBox}>
                    <div className={css.progressContainer}>
                      <progress value={awsFile?.fileProgressPercentage} max={100}>

                      </progress>
                      <div className={css.progressLabel}>
                        {awsFile?.fileProgressPercentage}% Complete
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  onDrop={event => handleDropAWS(event)}
                  onDragOver={event => event.preventDefault()}
                >
                  <FieldAddImage
                    id="addFile"
                    name="addFile"
                    accept={ACCEPT_otherfiles}
                    label={
                      <span className={css.chooseImageText}>
                        <span className={css.chooseImage}>
                          <FormattedMessage id="EditListingPhotosForm.chooseImage" />
                        </span>
                        <span className={css.imageTypes}>
                          <FormattedMessage id="EditListingPhotosForm.imageTypes" />
                        </span>
                      </span>
                    }
                    labelOne={<FormattedMessage id="EditListingPhotosForm.chooseFile" />}
                    labelTwo={<FormattedMessage id="EditListingPhotosForm.fileTypes" />}
                    type="file"
                    disabled={state.imageUploadRequested}
                    formApi={form}
                    onImageUploadHandler={handleAwsFiles}
                    aspectWidth={aspectWidth}
                    aspectHeight={aspectHeight}
                  />
                </div>
              )}
              {fileUrls &&
                fileUrls.length > 0 &&
                fileUrls.map((file, index) => {
                  const fileName = file.key.split('-').pop();
                  return (
                    <div key={index} className={css.documentWrapper}>
                      {removingIndex == index && awsFile?.awsFileRemoveInProgress ? (
                        <div className={css.loaderContent}>
                          <div>Romoving...</div>
                          <Loader />
                        </div>
                      ) : (
                        <div className={css.contentsBox}>
                          <img src={fileIcon} alt="fileIcon" width={50} height={50} />
                          <div title={fileName}>
                            File Name:
                            {fileName && fileName.length > 15
                              ? fileName.substring(0, 15) + '...'
                              : fileName}
                          </div>
                          <button
                            className={css.removeImage}
                            type="button"
                            onClick={() => removeAwsFiles(file?.key, index)}
                          >
                            {' '}
                            <IconCard brand="close" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
            {/*<p className={css.tip}>*/}
            {/*  <FormattedMessage id="EditListingPhotosForm.awsFileTip" />*/}
            {/*</p>*/}
            <p className={css.tip}>
              <FormattedMessage id="EditListingPhotosForm.awsFileTip2" />
            </p>

            {imagesError ? <div className={css.arrayError}>{imagesError}</div> : null}

            {/* <ImageUploadError
              uploadOverLimit={uploadOverLimit}
              uploadImageError={uploadImageError}
            /> */}

            <PublishListingError error={publishListingError} />
            <ShowListingsError error={showListingsError} />

            <Button
              className={css.submitButton}
              type="submit"
              inProgress={submitInProgress}
              // disabled={submitDisabled}
              ready={submitReady}
            >
              {saveActionMsg}
            </Button>
            {cloudImage?.cloudImageInProgress && (
              <div className={css.loader_overlay}>
                <div className={css.loader}></div>
              </div>
            )}
          </Form>
        );
      }}
    />
  );
};

EditListingPhotosFormComponent.defaultProps = { fetchErrors: null };

EditListingPhotosFormComponent.propTypes = {
  fetchErrors: shape({
    publishListingError: propTypes.error,
    showListingsError: propTypes.error,
    uploadImageError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  intl: intlShape.isRequired,
  onImageUpload: func.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  onRemoveImage: func.isRequired,
  listingImageConfig: object.isRequired,
};

export default compose(injectIntl)(EditListingPhotosFormComponent);
